import { Event, TicketV2 } from "@markit/common.types";
import { Colors } from "../../utils/colors";
import {
  getGoingCapacity,
  handlePluralString,
  isEventRelatedType,
  isEventTicketsPaid,
  isProductSpecificType,
  productItemLabel,
} from "@markit/common.utils";
import Skeleton from "react-loading-skeleton";
import { FormatCurrency } from "../../utils/FormatCurrency";
import { useTheme } from "../../hooks/useTheme";
import { useMemo, useState } from "react";
import { EventAnalyticsTicketRow } from "./EventAnalyticsTicketRow";
import {
  getRedeemedTicketsDistributed,
  getTotalAssignedOrganizers,
  getTotalRedeemed,
  getTotalTicketsDistributed,
  getTotalTicketsSold,
} from "../../utils/eventUtils/eventUtils";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import { EventPromoterAnalytics } from "./EventPromoterAnalytics";

type TicketAnalyticsPanelProps = {
  event: Event;
  userTicket: TicketV2 | undefined;
  totalScanned: number;
  totalGuests: number;
  totalRevenue: number | null;
  totalTips: number | null;
  totalLinkViews: number;
  dashboardVisibility: number;
  isPaymentCollectorRole: boolean;
};

export const TicketAnalyticsSection = (props: TicketAnalyticsPanelProps) => {
  const {
    event,
    userTicket,
    totalScanned,
    totalGuests,
    totalLinkViews,
    totalRevenue,
    totalTips,
    dashboardVisibility,
    isPaymentCollectorRole,
  } = props;

  // Payment collectors should see the full revenue/breakdown analytics like the
  // host, since they're the one actually collecting the money for this event.
  const showFullAnalytics = dashboardVisibility >= 2 || isPaymentCollectorRole;
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);

  const [totalRedeemed, setTotalRedeemed] = useState<number>(0);
  const [ticketsCapacity, setTicketsCapacity] = useState(0);

  const [ticketsSoldNotFromHost, setTicketsSoldNotFromHost] = useState(0);
  const [hostTicketsRedeemed, setHostTicketsRedeemed] = useState(0);
  const [hostTicketsDistributed, setHostTicketsDistributed] = useState(0);

  const styles = {
    analyticsContent: {
      gap: 24,
      color: Colors.BLACK,
    },

    analyticsText: {
      fontSize: 32,
    },

    containerView: {
      paddingInline: 14,
    },

    contentView: {
      paddingBottom: 14,
    },

    headerTitle: {
      paddingTop: 14,
      fontSize: 16,
      fontWeight: 600,
    },

    overallContainerView: {
      width: "100%",
      backgroundColor: theme.SecondaryBG.backgroundColor,
      borderRadius: 20,
    },

    sectionHeaderText: {
      fontSize: 16,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },

    subText: {
      color: theme.SubText.color,
      fontSize: 12,
    },
  };

  // TODO: Create a custom hook wrapper that can load all the required data, so we don't need to pass it as props throughout
  useAsyncOnMount(async () => {
    const [
      totalRedeemed,
      totalAssignedOrganizers,
      ticketsSoldNotFromHost,
      ticketsCapacity,
      hostTicketsRedeemed,
      hostTicketsDistributed,
    ] = await Promise.all([
      getTotalRedeemed(event.id),
      getTotalAssignedOrganizers(event.id),
      getTotalTicketsSold(event.id),
      getGoingCapacity(event),
      getRedeemedTicketsDistributed(event.id),
      getTotalTicketsDistributed(event.id),
    ]);
    setTotalRedeemed(Math.max(totalRedeemed - totalAssignedOrganizers, 0));
    setTicketsSoldNotFromHost(ticketsSoldNotFromHost);
    setTicketsCapacity(ticketsCapacity);
    setHostTicketsRedeemed(hostTicketsRedeemed);
    setHostTicketsDistributed(hostTicketsDistributed);
    setLoading(false);
  });

  const itemLabel = useMemo(
    () => productItemLabel(event.eventType),
    [event.eventType]
  );

  const displayTicketsAvailable = useMemo(
    () => getGoingCapacity(event) !== 0,
    [event]
  );

  return (
    <div className="ColumnNormal" style={{ gap: 24 }}>
      <div className="ColumnNormal" style={{ gap: 14 }}>
        <div style={styles.sectionHeaderText}>Analytics</div>
        <StandardBorderedContainer
          containerStyles={styles.overallContainerView}
        >
          <div className="ColumnNormal">
            <div style={{ ...styles.headerTitle, ...styles.containerView }}>
              {isProductSpecificType(event.eventType) ? "Orders" : "Ticketing"}
            </div>
            <HorizontalDivider altMargin={14} />
            <div style={{ ...styles.contentView, ...styles.containerView }}>
              <div className="RowNormal" style={styles.analyticsContent}>
                {isEventRelatedType(event.eventType) ? (
                  <div>
                    <div style={styles.analyticsText}>
                      {loading ? (
                        <Skeleton />
                      ) : (
                        totalScanned + "/" + totalGuests
                      )}
                    </div>
                    <div style={styles.subText}>Tickets Scanned</div>
                  </div>
                ) : null}
                {showFullAnalytics && isEventTicketsPaid(event) ? (
                  <>
                    <div>
                      <div style={styles.analyticsText}>
                        {totalRevenue === null ? (
                          <Skeleton />
                        ) : (
                          FormatCurrency(totalRevenue)
                        )}
                      </div>
                      <div style={styles.subText}>
                        {itemLabel.singularCap} Revenue
                      </div>
                    </div>
                    {event.tippingEnabled ? (
                      <div>
                        <div style={styles.analyticsText}>
                          {totalTips === null ? (
                            <Skeleton />
                          ) : (
                            FormatCurrency(totalTips)
                          )}
                        </div>
                        <div style={styles.subText}>Tips Collected</div>
                      </div>
                    ) : null}
                  </>
                ) : null}
                <div>
                  <div style={styles.analyticsText}>
                    {loading ? <Skeleton /> : totalLinkViews}
                  </div>
                  <div style={styles.subText}>Link Views</div>
                </div>
              </div>
              {showFullAnalytics ? (
                <>
                  <hr />
                  <div style={{ color: Colors.BLACK }}>
                    <div
                      style={{
                        color: Colors.BLACK,
                        fontSize: "14px",
                        fontWeight: "600",
                        marginBottom: "14px",
                      }}
                    >
                      {itemLabel.singularCap} Breakdown
                    </div>
                    <div style={{ display: "flex" }}>
                      <div
                        style={{
                          fontSize: "24px",
                          marginRight: "14px",
                          minWidth: 45,
                        }}
                      >
                        {totalGuests}
                      </div>
                      <div>
                        <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                          Total {itemLabel.pluralCap}
                        </div>
                        <div
                          style={{
                            color: Colors.GRAY1,
                            fontSize: "12px",
                            marginBottom: "4px",
                          }}
                        >
                          Used: {ticketsSoldNotFromHost}
                          {displayTicketsAvailable ? "/" + ticketsCapacity : ""}
                        </div>
                        <div style={{ color: Colors.GRAY1, fontSize: "12px" }}>
                          Free {itemLabel.pluralCap} Redeemed:{" "}
                          {hostTicketsRedeemed}/{hostTicketsDistributed}
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr />
                  {event.customTickets.length > 1 && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "14px",
                        }}
                      >
                        {event.customTickets.map((ticket) => {
                          return (
                            <EventAnalyticsTicketRow
                              key={ticket.id}
                              event={event}
                              userId={event.createdBy}
                              customTicket={ticket}
                            />
                          );
                        })}
                      </div>
                      <hr />
                    </>
                  )}
                  <div style={{ color: Colors.BLACK }}>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        marginBottom: "14px",
                      }}
                    >
                      {itemLabel.entityCap} Audience
                    </div>
                    <div style={{ display: "flex" }}>
                      <div
                        style={{
                          fontSize: "24px",
                          marginRight: "14px",
                          minWidth: 45,
                        }}
                      >
                        {loading ? <Skeleton /> : totalRedeemed}
                      </div>
                      <div>
                        <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                          Phone Numbers Captured
                        </div>
                        <div style={{ color: Colors.GRAY1, fontSize: "12px" }}>
                          You can text {totalRedeemed} unique{" "}
                          {handlePluralString("number", totalRedeemed)} from
                          this {itemLabel.entity}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </StandardBorderedContainer>
      </div>
      {!isPaymentCollectorRole &&
      isEventTicketsPaid(event) &&
      isEventRelatedType(event.eventType) ? (
        <EventPromoterAnalytics event={event} userTicket={userTicket} />
      ) : null}
    </div>
  );
};
